import instance from "components/common/HandleApiFailure";
import jwtDecode from "jwt-decode";
import { uniq } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { axiosPostToken, getServiceType } from "utils/APIHelper";
import {
  ABOVE_PROP_USER_TYPE,
  americanRegion,
  dummyInnCodes,
  missingIncodeText,
  staticInncodesEnv
} from "utils/CommonConstants";
import { decrypt, encrypt, getIsCorpUser, handleUnauthorizedAccess } from "utils/CommonFunctions";
import { TOKEN_URL } from "../../../utils/URLConstants";
import RedirectToGenDialog from "./RedirectToGenDialog";

interface Props {
  isLoggedIn?: boolean;
  setIsLoggedIn?: any;
}

function Login(props: Props) {
  const { setIsLoggedIn } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [isNonAmericaUser, setIsNonAmericaUser] = useState(false);
  const [isAmericaGenUser, setIsAmericaGenUser] = useState(false);

  const redirectToLogin = useCallback(() => {
    setIsLoggedIn(false);
    handleUnauthorizedAccess();
  }, [setIsLoggedIn, location.pathname]);

  const handleAmericanUsers = () => {
    if (getIsCorpUser(decrypt("salt", localStorage.getItem("lobbyUserType")))) {
      setIsAmericaGenUser(true);
    } else {
      redirectToBoost();
    }
  };

  const handleNonAmericanUsers = () => {
    if (getIsCorpUser(decrypt("salt", localStorage.getItem("lobbyUserType")))) {
      setIsAmericaGenUser(true);
    } else {
      redirectToGen();
    }
  };

  const handleButtonClick = (redirectTo: string) => {
    if (redirectTo === "Boost") {
      redirectToBoost();
    } else {
      setIsAmericaGenUser(false);
      redirectToGen();
    }
  };

  const redirectToGen = () => {
    setIsNonAmericaUser(true);
    setTimeout(() => {
      window.location.replace(process.env.REACT_APP_GEN_LOGIN_URL ? process.env.REACT_APP_GEN_LOGIN_URL : "");
    }, 10000);
  };

  const redirectToBoost = () => {
    const decoded = jwtDecode(localStorage.getItem("id_token") ?? "");
    setDataInLocalStorage(decoded);
    setIsLoggedIn(true);
    const url = localStorage.getItem("url");
    if (url) navigate(url);
  };

  const getDefaultInncode = (data: any) => {
    if (data["inncode"] && !dummyInnCodes.includes(data["inncode"])) return data["inncode"];
    else if (data["innCodes"]?.length > 0)
      return data["innCodes"]?.filter((item: any) => !dummyInnCodes.includes(item)).sort()[0];
    else if (
      staticInncodesEnv.includes(process.env.REACT_APP_ACTIVE_ENV || "") &&
      String(data["lobbyUserType"]).toLowerCase() === ABOVE_PROP_USER_TYPE
    ) {
      return "ABEHW";
    } else return missingIncodeText;
  };

  const getInnCodeList = (list = [], inncode: string) => {
    let resultList: any;
    let combinedList = inncode ? uniq([...list, inncode]) : list;
    if (combinedList.length > 0) {
      resultList = combinedList.filter((item: string) => !dummyInnCodes.includes(item));
    } else {
      resultList = missingIncodeText;
    }
    return resultList;
  };

  const setDataInLocalStorage = (data: any) => {
    secureLocalStorage.setItem("un", data["displayName"]);
    secureLocalStorage.setItem("email", data["mail"]);
    secureLocalStorage.setItem("designation", data["hltPositionDescr"]);
    secureLocalStorage.setItem("hltOrganization", data["hltOrganization"]);
    localStorage.setItem("firstName", data["givenName"]);
    localStorage.setItem("lastName", data["sn"]);
    localStorage.setItem("hltInnCodes", getInnCodeList(data["innCodes"], data["inncode"]));
    localStorage.setItem("defaultInncode", getDefaultInncode(data));
    localStorage.setItem("expired_in", data["exp"]);
    localStorage.setItem("hltId", data["hltId"]);
  };

  const checkAndAddAdditionalAssociate = async (accessToken: string) => {
    const decoded: any = jwtDecode(accessToken);
    const params = {};
    await instance
      .get(`${getServiceType("user")}associates?size=1&hltIds=${decoded["hltId"]}`, {
        params,
        headers: { Authorization: "Bearer " + accessToken }
      })
      .then(async (response: any) => {
        if (Object.keys(response?.data?.users).length === 0) {
          const inncodeList = getInnCodeList(decoded["innCodes"], decoded["inncode"]);
          const userInfo = {
            hltId: decoded["hltId"],
            displayName: decoded["displayName"],
            firstName: decoded["givenName"],
            lastName: decoded["sn"],
            email: decoded["mail"],
            designation: decoded["hltPositionDescr"],
            innCodes: inncodeList === missingIncodeText ? [] : inncodeList,
            lobbyUserType: decoded["lobbyUserType"],
            countryCode: decoded["countryCode"]
          };
          await instance.post(`${getServiceType("user")}additional-associates`, userInfo, {
            headers: { Authorization: "Bearer " + accessToken }
          });
        }
      })
      .catch((error) => {
        console.log("Api error", error);
      });
  };

  useEffect(() => {
    const code = window.location.search.split("code=")[1];
    if (code) {
      axiosPostToken(TOKEN_URL, code)
        .then(async (response: any) => {
          const decoded: any = jwtDecode(response.data.access_token);
          await checkAndAddAdditionalAssociate(response.data.access_token);
          localStorage.setItem("id_token", response.data.access_token);
          localStorage.setItem("refresh_token", response.data.refresh_token);
          secureLocalStorage.setItem("tt", response.data.expires_in);
          localStorage.setItem("lobbyUserType", encrypt("salt", decoded["lobbyUserType"]));
          //logic for redirection for american and non american users

          if (americanRegion.includes(decoded["countryCode"])) {
            handleAmericanUsers();
          } else {
            handleNonAmericanUsers();
          }
        })
        .catch((error) => {
          console.log("Login error", error);
        });
    } else {
      redirectToLogin();
    }
  }, [navigate, redirectToLogin, setIsLoggedIn]);

  return (
    <>
      {isNonAmericaUser && (
        <RedirectToGenDialog
          type="Nonamerican"
          title="Welcome!"
          description="Redirecting to GEN. You may need to re-enter your login to access the platform"
        />
      )}
      {isAmericaGenUser && (
        <RedirectToGenDialog
          type="American"
          title="Welcome!"
          description="Select the Hilton Action Planning Tool you would like to access."
          handleOnClick={handleButtonClick}
        />
      )}
    </>
  );
}

export default Login;
